<template>
  <main class="content">
    <Hero title="Accountgegevens aanpassen"/>
    <main class="main maxw--waist block">
      <form @submit.prevent="submit" class="form">
        <fieldset class="form__fieldset">
          <div class="form__row">
            <FormTextField class="field--w1-2" name="firstname" label="Voornaam" v-model="v$.firstname.$model" :validator="v$.firstname"/>
            <FormTextField class="field--w1-2" name="lastname" label="Achternaam" v-model="v$.lastname.$model" :validator="v$.lastname"/>
          </div>
          <div class="form__row">
            <FormTextField name="email" type="email" label="Email" v-model="v$.email.$model" :validator="v$.email"/>
          </div>
          <div class="form__row">
            <div class="field">
              <button class="btn btn--full" type="submit" :disabled="isLoading || isSame">
                Aanpassen
                <Spinner v-if="isLoading" />
              </button>
            </div>
          </div>
          <div class="form__row" v-if="!neutral">
            <span class="field__status text--center" :class="feedbackClass">{{ feedbackMessage }}</span>
          </div>
        </fieldset>
      </form>
      <div class="vertical-center">
        <!-- <nuxt-link to="/account/wachtwoord-wijzigen" class="btn btn--flat mb-1">Wachtwoord aanpassen</nuxt-link> -->
        <nuxt-link to="/account" class="btn btn--flat">Terug naar account</nuxt-link>
      </div>
    </main>
  </main>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';
import type { User } from '@/typings/strapi/objects/user';

useHead({
  title: 'Accountgegevens aanpassen'
});

const client = useStrapiClient();
const user = useStrapiUser<User>();
const { setUser } = useStrapiAuth();

const { isLoading, startLoading, stopLoading } = useLoading();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Accountgegevens gewijzigd',
  'Er is iets fout gegaan. Probeer het later opnieuw'
);

const baseFormData = computed(() => ({
  firstname: user.value?.firstname || '',
  lastname: user.value?.lastname || '',
  email: user.value?.email || ''
}))

const formData = reactive({ ...baseFormData.value });

const isSame = computed(() => {
  return baseFormData.value.email === formData.email
      && baseFormData.value.firstname === formData.firstname
      && baseFormData.value.lastname === formData.lastname;
})

async function submit(e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;

  startLoading();

  try {
    const response = await client<User>(`users/${user.value?.id}`, {
      body: formData,
      method: 'PUT'
    });

    // Update user if succesful
    await setUser(response);

    handleSuccess();
  } catch (e) {
    setStatus(false);
  } finally {
    stopLoading();
  }
}

const rules = computed(() => ({
  firstname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  lastname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    email: helpers.withMessage('Je dient een geldig emailadres in te vullen.', email)
  },
}));

const v$ = useVuelidate(rules, formData);

definePageMeta({
  pageTransition: pageTransitionConfig,
});
</script>
